import type { CommonResponse, CommonListResponse } from './api'

export type PenetapanPegawaiState =
  | 'NOT_RATED'
  | 'NOT_SENT'
  | 'SENT_TO_PPK'
  | 'SENDING_TO_BKN'
  | 'SENT_TO_BKN'
  | 'ERROR_SEND'

/** Profile */
export type PenilikPegawaiRoleType = 'PENILIK' | 'TK' | 'PPK'
export type PenilikPegawaiProfile = {
  nama: string
  roles: Array<PenilikPegawaiRoleType>
  namaWilayah?: string // Eg. Provinsi Jawa Barat
  namaLembaga?: string // Eg. Dinas Pendidikan dan Olahraga Provinsi Jawa Barat
}

/** Period */
export type PenilikPeriod = {
  periodeId: string
  startDate: string
  endDate: string
}
type PeriodDetailDate = {
  startDate: string
  endDate: string
}
export type ResponseSkpPenilikPeriodV2 = CommonResponse<PenilikPeriod[]>
export type ResponseSkpPenilikPeriodDetail = {
  skp: PeriodDetailDate
  perencanaan: PeriodDetailDate
  pelaksanaan: PeriodDetailDate
  penetapan: PeriodDetailDate
}

/** Perencanaan */
// Perencanaan: Status
// source: https://gitlab.com/wartek-id/guru/guru-pps-be/-/blob/main/app/usecase/perencanaan/statuses.go
export const StatusPerencanaanList = [
  'MULAI_PERENCANAAN',
  'SIAP_DIAJUKAN',
  'SEDANG_DIPERIKSA',
  'DIARAHKAN_REVISI_TK',
  'DIARAHKAN_REVISI_PPK',
  'DIKIRIM_TK',
  'DISETUJUI_PPK',
  'PELAKSANAAN_DIMULAI',
] as const

// E-Kinerja Sync Status
// source: https://gitlab.com/wartek-id/guru/guru-pps-be/-/blob/main/app/usecase/perencanaan/trigger_sync_to_ekin.go
export const StatusEkinSyncList = [
  'INIT',
  'SKP_FAILED',
  'SKP_CREATED',
  'RHK_FAILED',
  'RHK_CREATED',
  'PERSETUJUAN_FAILED',
  'PERSETUJUAN_SUCCESS',
] as const

// Perencanaan: Summary & Submission
export type StatusPerencanaan = typeof StatusPerencanaanList[number]
export type StatusEkinSync = typeof StatusEkinSyncList[number]

export type PenilikSummary = {
  skpId: string
  isRhkAtasanExists: boolean
  pejabatPenilaiKinerja: string
  perencanaanStatus: StatusPerencanaan
  pelaksanaanStatuses: StatusPelaksanaan
  sekolahBinaanCount: number
  ekinSyncErrorMessage: string
  ekinSyncStatus: StatusEkinSync
  ekinSyncAt: string
}

export type PenilikStatus = Pick<
  PenilikSummary,
  'skpId' | 'perencanaanStatus' | 'pelaksanaanStatuses'
> & {
  penetapanStatus: PenetapanPegawaiState
}

export type PerilakuKerjaAspek = {
  aspek: string
  indikator?: string
  ekspektasi?: string
}

export type PerilakuKerjaPenilaian = {
  perwujudan: string
  dampak: string
}

export type PerencanaanSummary = {
  skpId: string
  periodeId: string
  praktikKinerjaStatus: StatusPerencanaan
  perilakuKerjaStatus: StatusPerencanaan
  pengembanganKompetensiStatus: StatusPerencanaan
  catatanRevisiPerencanaan: string
}

export type Perencanaan = PerencanaanSummary & {
  penilikId: string
  praktikKinerjaRhk: Array<{
    id: string
    nama: string
    keterangan: string
  }>
  praktikKinerjaSatuanPendidikan: Array<{
    npsn: string
    satuanPendidikan: string
    scoreMetric: string
  }>
  perilakuKerjaValues: string
  pengembanganKompetensiIndikator: {
    indikatorId: string
    section: string
    deskripsi: string
  }
  pengembanganKompetensiKegiatanValues: Array<{
    kategori: {
      id: string
      nama: string
    }
    kegiatan: {
      id: string
      jenisKegiatan: string
    }
    tempatMeningkatkanIndikator: string
  }>
  namaPenilik?: string
}

// Perencanaan: Submission
export type ResponsePerencanaanSummary = CommonResponse<PerencanaanSummary>

export type ResponsePerencanaanSubmission = {
  message: string
}

// Pelaksanaan Status
// source: https://gitlab.com/wartek-id/guru/guru-pps-be/-/blob/main/app/usecase/pelaksanaan/statuses.go
export const StatusPelaksanaanPengembanganKompetensiList = [
  'MULAI_REFLEKSI',
  'SUDAH_REFLEKSI',
] as const
export const StatusPelaksanaanPerilakuKerjaList = [
  'MULAI_LAKSANAKAN',
  'SUDAH_DINILAI',
] as const
export const StatusPelaksanaanPraktikKinerjaList = [
  'DISKUSI_PERSIAPAN',
  'PENDAMPINGAN_SEKOLAH',
  'DISKUSI_TINDAK_LANJUT',
  'DISKUSI_TINDAK_LANJUT_PENILAIAN',
  'UPAYA_TINDAK_LANJUT',
  'REFLEKSI_TINDAK_LANJUT',
  'REFLEKSI_TINDAK_LANJUT_PENILAIAN',
  'SELESAI',
] as const
export const StatusPelaksanaanDokumenAkuntabilitasList = [
  'CENTANG_KETERSEDIAAN_DOKUMEN',
  'DOKUMEN_TERSEDIA',
  'DOKUMEN_TERSEDIA_TK',
] as const
export type StatusPelaksanaanPengembanganKompetensi =
  typeof StatusPelaksanaanPengembanganKompetensiList[number]
export type StatusPelaksanaanPerilakuKerja =
  typeof StatusPelaksanaanPerilakuKerjaList[number]
export type StatusPelaksanaanPraktikKinerja =
  typeof StatusPelaksanaanPraktikKinerjaList[number]

type MetadataForStatus<T extends StatusPelaksanaanPraktikKinerja> =
  T extends 'DISKUSI_TINDAK_LANJUT' ? { isPenilaian: boolean } : never

export type StatusPelaksanaanPraktikKinerjaMetadata = {
  [K in StatusPelaksanaanPraktikKinerja]?: MetadataForStatus<K>
}

export type StatusPelaksanaanDokumenAkuntabilitas =
  typeof StatusPelaksanaanDokumenAkuntabilitasList[number]
export type StatusPelaksanaanUnion =
  | StatusPelaksanaanPengembanganKompetensi
  | StatusPelaksanaanPerilakuKerja
  | StatusPelaksanaanPraktikKinerja
  | StatusPelaksanaanDokumenAkuntabilitas
export type StatusPelaksanaan = {
  pengembanganKompetensi: StatusPelaksanaanPengembanganKompetensi
  perilakuKerja: StatusPelaksanaanPerilakuKerja
  praktikKinerja: StatusPelaksanaanPraktikKinerja
  pelaksanaanTugas: StatusPelaksanaanDokumenAkuntabilitas
  praktikKinerjaPerubahanStatus?: StatusPelaksanaanPraktikKinerja
}

export type RequestPerencanaanBody = {}

export type RequestPerencanaanPerilakuKerjaBody = {
  periodeId: string
  perilakuKerjaValues: string
}

export type RequestMulaiPelaksanaanBody = {
  periodeId: string
}

export type ResponseSyncToEkin = {
  syncStatus: string
}

// Perencanaan: Praktik Kinerja
export type PraktikKinerjaItem = {
  id: string
  tipe?: string
  nama: string
  definisi?: string
  keterangan: string
  iconUrl: string
  badge: string
}
export type SatuanPendidikanItem = {
  npsn?: string
  satuanPendidikan: string
  scoreMetric: string
}
export type PraktikKinerjaForm = {
  selectedPraktikKinerjaIds: string[]
  selectedSatuanPendidikanIds: string[]
}
export type PerencanaanPraktikKinerjaRequestBody = {
  periodeId: string
  praktikKinerjaRhkIds?: string[]
  praktikKinerjaNpsn?: string[]
}

export type RHKOptionType = 'praktik_kinerja'

// Perencanaan: Pengembangan Kompetensi
export type PengembanganKompetensiIndikatorGroup =
  | 'none'
  | 'needImprovement'
  | 'strong'
export type PengembanganKompetensiIndikator = {
  indikatorId: string
  section: string
  deskripsi: string
}
export type PengembanganKompetensiIndikatorOptions = Record<
  PengembanganKompetensiIndikatorGroup,
  Array<PengembanganKompetensiIndikator>
>
export type PengembanganKompetensiKategori = {
  id: string
  nama: string
  isInitiallyShown: boolean
}
export type PengembanganKompetensiKategoriOption = {
  id: string
  name: string
  isInitiallyShown: boolean
  disabled: boolean
}
export type PengembanganKompetensiKegiatan = {
  id: string
  kategoriId: string
  jenisKegiatan: string
  buktiDukung: string
  poin: number
}
export type PengembanganKompetensiKegiatanOption = {
  id: string
  name: string
  poin: number
  disabled: boolean
}
export type PengembanganKompetensiLokasi =
  | 'DI_DALAM_LINGKUNGAN_DINAS_PENDIDIKAN'
  | 'DI_LUAR_LINGKUNGAN_DINAS_PENDIDIKAN'
export type PengembanganKompetensiForm = {
  indicator: string
  activities: Array<{
    kategori: string | undefined
    kegiatan: string | undefined
    lokasi: PengembanganKompetensiLokasi | undefined
    poin: number
  }>
}
export type PerencanaanPengembanganKompetensiActivity = {
  kategoriId: string
  kegiatanId: string
  tempatMeningkatkanIndikator: string
}
export type RequestPerencanaanPengembanganKompetensi = {
  periodeId: string
  pengembanganKompetensiIndikatorId: string
  pengembanganKompetensiKegiatanValues: PerencanaanPengembanganKompetensiActivity[]
}

// Perencanaan: Reset
export type ResetSKPPhase = 'PERENCANAAN' | 'PELAKSANAAN'

export type PerencanaanSubmissionResponse = {
  message: string
}

/** Pelaksanaan */
export type SubmissionResponse = {
  message: string
}

export type PelaksanaanPerilakuKerja = {
  perilakuKerjaValues: string
  perilakuKerjaPenilaianValues: string
  ratingPerilakuKerja: string
  namaPenilik: string
  dinasPendidikan?: string
  tahun?: string
}

export type PelaksanaanPengembanganKompetensi = {
  pengembanganKompetensiIndikator: PengembanganKompetensiIndikator
  pengembanganKompetensiKegiatanValues: Array<{
    kategori: {
      id: string
      nama: string
    }
    kegiatan: {
      id: string
      jenisKegiatan: string
    }
    tempatMeningkatkanIndikator: string
  }>
  pengembanganKompetensiPelaksanaanInspirasi: string
  pengembanganKompetensiPelaksanaanPenerapanInspirasi: string
  isEditable: boolean
  pelaksanaanPengembanganKompetensiUpdatedAt: string
  namaPenilik: string
  dinasPendidikan?: string
  tahun?: number
}

export type PenilaianPengembanganKompetensiSubmissionResponse = {
  message: string
}

export type PenilaianPengembanganKompetensiSubmissionBody = {
  pengembanganKompetensiPelaksanaanInspirasi: string
  pengembanganKompetensiPelaksanaanPenerapanInspirasi: string
}

export type RhkPenilik = {
  id: string
  nama: string
  keterangan: string
  iconUrl?: string
  badge?: string
}

type SatuanPendidikan = {
  npsn: string
  satuanPendidikan: string
}

export type PenilaianPraktikKinerjaFormValues = {
  formValues: string
  rating: string
}

export type PraktikKinerjaDiskusiPersiapanFormValue = {
  tantangan: string
  isu: string
  npsn_satdik_pendamping: string
  strategi: string
}

export type PraktikKinerjaPendampinganFieldType =
  | 'pengalaman_mengesankan'
  | 'pengalaman_sulit'
  | 'pengalaman_memuaskan'
  | 'faktor_berhasil'
  | 'faktor_terhambat'
  | 'penyesuaian_sudah'
  | 'penyesuaian_akan'
  | 'upaya'

export type PraktikKinerjaPendampinganFormValue = {
  [key in PraktikKinerjaPendampinganFieldType]: string
}

export type PraktikKinerjaDiskusiTindakLanjutFormValue = {
  is_already_kumpulkan: boolean
}

export type PraktikKinerjaUpayaTindakLanjutFormValue = {
  realisasi: string
}

export type PraktikKinerjaRefleksiTindakLanjutFormValue = {
  inspirasi: string
  perubahan_praktik: string
  tantangan: string
  rencana: string
}

export type PraktikKinerjaFormName =
  | 'diskusi_persiapan'
  | 'pendampingan_sekolah'
  | 'diskusi_tindak_lanjut'
  | 'upaya_tindak_lanjut'
  | 'refleksi_tindak_lanjut'

export type PraktikKinerjaFormValuesRequestBody = {
  formName: PraktikKinerjaFormName
  formValues: string
  saveAsDraft: boolean
}

// Get Summary of Praktik Kinerja
export type PelaksanaanPraktikKinerjaSummary = {
  namaPenilik: string
  pelaksanaanPraktikKinerjaStatus: StatusPelaksanaanPraktikKinerja
  rhk: RhkPenilik[]
  satuanPendidikan: SatuanPendidikan[]
}

// Get Dokumen Akuntabilitas
export type PelaksanaanTugasResponse = {
  dokumenTersedia: boolean
  dokumenTersediaAtasan: boolean
}

export type PraktikKinerjaSurveyPendampingan = {
  namaPenilik: string
  rating: string
  rhk: RhkPenilik[]
}

export type PraktikKinerjaSurveyPendampinganRating = {
  catatan: string
  rating: {
    aspek: number
    value: number
  }[]
}

// Notification
export type NotificationListItem = {
  id: number
  type: string
  title: string
  description: string
  contents: {
    pelaksanaanStatus?: StatusPelaksanaanPraktikKinerja
    perubahanStatus?: StatusPelaksanaanPraktikKinerja
    namaPenilik?: string
  }
  results: {
    confirmationState?: string
  }
  read: boolean
  date: string
  skpId: string
}

export type ResponseGetNotificationList =
  CommonListResponse<NotificationListItem>

/**
 * Bukti Dukung Periodik
 */
export type ResponseGetBuktiDukungPeriodikYearList = {
  tahun: Array<string>
}
export type ResponseGetBuktiDukungPeriodikMonthList = {
  buktiDukung: Array<{
    nama: string
    bulan: number
  }>
}
